import { useCallback, useContext, useMemo, useState } from "react";
import { message, notification } from "@findemdev/maui";
import { decodeString } from "@findemdev/findem-helpers";
import { ICompanyList } from "../types/common";
import { useShareWithOrgMutation } from "../../../services/company";
import UserContext from "../../../contexts/UserContext";
import { emitGetroEvent } from "../utils/emitGetroEvent";
import { useAppDispatch } from "../../CredentialApp/hooks/storeHooks";
import { updateCompanyList } from "../reducers/companyListsSlice";

export const useShareWithOrg = (companyList: ICompanyList | undefined) => {
  const userModel = useContext(UserContext);
  const accountId = userModel.getUser().account_id;
  const hasGetroNetwork = userModel.hasGetroNetwork();

  const dispatch = useAppDispatch();
  const [shareWithOrgMutation, { isLoading: isShareLoading }] = useShareWithOrgMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [unshareModalOpen, setUnshareModalOpen] = useState(false);
  const [sharedGetroListId, setSharedGetroListId] = useState<string | null>(null);

  const isSharedWithOrg = useMemo(() => {
    if (!companyList) return false;
    return companyList.collaborators.some((c) => c.entity_id === accountId);
  }, [accountId, companyList]);

  const shareDirectly = useCallback(async () => {
    if (!companyList) return;
    const name = decodeString(companyList.name);
    try {
      const wasShared = isSharedWithOrg;
      const response = await shareWithOrgMutation({ joid: companyList.uid, ref: companyList.ref }).unwrap();
      const result = (response as any)?.data ?? response;
      dispatch(updateCompanyList(result));

      emitGetroEvent('companyBuilder', wasShared ? 'project:deleted' : 'project:created', {
        projectRef: companyList.ref,
        name,
        getroListId: result?.getro_list_id ?? companyList.getro_list_id,
      });

      notification.success({ message: wasShared ? `Successfully unshared ${name}` : `Successfully Shared ${name} with Org` });
    } catch {
      notification.error({ message: `Failed to Share ${name} with Org` });
    }
  }, [companyList, shareWithOrgMutation, isSharedWithOrg]);

  const handleShareAndContinue = useCallback(async (onSuccess?: ((getroListId: string) => void) | unknown) => {
    if (!companyList) return;
    try {
      const wasShared = isSharedWithOrg;
      const response = await shareWithOrgMutation({ joid: companyList.uid, ref: companyList.ref }).unwrap();
      const result = (response as any)?.data ?? response;
      dispatch(updateCompanyList(result));
      const listId = result?.getro_list_id ?? companyList.getro_list_id;

      if (result?.getro_list_id) {
        setSharedGetroListId(result.getro_list_id);
      }

      emitGetroEvent('companyBuilder', wasShared ? 'project:deleted' : 'project:created', {
        projectRef: companyList.ref,
        name: decodeString(companyList.name),
        getroListId: listId,
      });

      setModalOpen(false);

      if (typeof onSuccess === 'function' && listId) {
        onSuccess(listId);
      }
    } catch {
      message.error("Failed to share list with org");
    }
  }, [companyList, shareWithOrgMutation, isSharedWithOrg]);

  const handleStopSharing = useCallback(async () => {
    if (!companyList) return;
    const name = decodeString(companyList.name);
    try {
      const response = await shareWithOrgMutation({ joid: companyList.uid, ref: companyList.ref }).unwrap();
      const result = (response as any)?.data ?? response;
      dispatch(updateCompanyList(result));

      emitGetroEvent('companyBuilder', 'project:deleted', {
        projectRef: companyList.ref,
        name,
        getroListId: result?.getro_list_id ?? companyList.getro_list_id,
      });

      notification.success({ message: `Successfully unshared ${name}` });
      setUnshareModalOpen(false);
    } catch {
      notification.error({ message: `Failed to unshare ${name}` });
    }
  }, [companyList, shareWithOrgMutation]);

  const openShareModal = useCallback(() => setModalOpen(true), []);
  const closeShareModal = useCallback(() => setModalOpen(false), []);
  const openUnshareModal = useCallback(() => setUnshareModalOpen(true), []);
  const closeUnshareModal = useCallback(() => setUnshareModalOpen(false), []);

  const handleToggle = useCallback(() => {
    if (isSharedWithOrg) {
      if (hasGetroNetwork) {
        openUnshareModal();
      } else {
        shareDirectly();
      }
    } else {
      if (hasGetroNetwork) {
        openShareModal();
      } else {
        shareDirectly();
      }
    }
  }, [isSharedWithOrg, hasGetroNetwork, openShareModal, openUnshareModal, shareDirectly]);

  return {
    isSharedWithOrg,
    hasGetroNetwork,
    getroListId: companyList?.getro_list_id ?? sharedGetroListId,
    modalOpen,
    unshareModalOpen,
    isLoading: isShareLoading,
    openShareModal,
    closeShareModal,
    closeUnshareModal,
    handleToggle,
    handleShareAndContinue,
    handleStopSharing,
  };
};
